export const meta = {
  title: 'Dogstar',
  subtitle: 'episodic content at social media speed',
  category: 'case-study',
  date: '2026-02-07',
  hero: {
    type: 'video',
    cdnKey: 'dogstar_1.webm',
  },
  slug: 'dogstar',
};

## An industry veteran chooses a different path

<InlineMedia cdnKey="blog_dogstar_mark_andrews.jpg" alt="Mark Andrews" />
As an Academy Award–winning director and longtime leader at Pixar, **Mark
Andrews** had already proven what traditional animation pipelines could achieve
at their highest level. With **Dogstar**, a sci-fi action series about
anthropomorphic special-ops dogs in a PG-13 / R-rated universe, he set out to
work differently. Instead of launching through a traditional studio or streamer,
Mark and his team chose to build the project in public—releasing short episodic
content on YouTube and social platforms to grow an audience ahead of the full
series.

The goal wasn’t promotional filler. It was to create real narrative material that introduced the characters, tone, and world of Dogstar in a format native to social platforms—fast, responsive, and alive in the moment.

<ArticleVideo cdnKey="dogstar_1.webm" />

## The challenge: studio-quality characters on social timelines

Mark’s ambition created a fundamental production challenge. He wanted animation that felt authored and character-driven, but could be produced fast enough to respond to cultural moments, trending topics, and audience feedback in near real time. <br />Traditional CGI pipelines make this extremely difficult:

- Motion capture requires expensive stages, suits, and specialized crews
- Standard animation workflows involve long iteration cycles across layout, animation, lighting, and rendering
- For independent teams, production costs and marketing budgets quickly become prohibitive

The objective wasn’t to replace high-fidelity animation for the eventual series. It was to complement it with a new category of content:

- Designed to be timely rather than timeless
- Meant to be watched once, shared, and reacted to
- Built for social velocity rather than archival perfection

A form of episodic animation that could live between concept development and final production—and still feel intentional.

<ArticleVideo cdnKey="dogstar_2.webm" />

> Holy cow—if we give Cartwheel the assets we already made and just film ourselves doing stuff, how much content can we actually make?

## The solution: turning reference into production

Mark saw an opportunity to collapse a long-standing gap in animation workflows: the distance between reference and final motion. The Dogstar team ran a one-week production sprint using Cartwheel’s **Comic (video-to-motion)** model, integrated with Unreal Engine.

<ArticleVideo cdnKey="dogstar_3.webm" />

The streamlined process:

1. **Shoot reference video** - Mark and the team acted out scenes using phone cameras—no mocap suits, no stage, no setup overhead.
2. **Retarget with Cartwheel** - Comic 3 retargeted raw video reference directly into high-quality character motion on the Dogstar assets.
3. **Render in Unreal** - Real-time rendering removed traditional lighting, compositing, and offline rendering bottlenecks.

What was once “reference” became production-ready animation, compressing weeks of work into days.

> It turns a video into motion capture data that’s on par with a lot of mocap, but you don’t need a stage. Just your phone.

<MediaGrid
  items={[
    { type: 'video', cdnKey: 'dogstar_4.webm' },
    { type: 'video', cdnKey: 'dogstar_5.webm' },
  ]}
/>

## The impact: A new model for animated storytelling

The experiment validated a fundamentally different approach to animated content:

- **Pipeline speed** - Production cycles dropped from weeks to days, enabling sustainable episodic releases.
- **Cost efficiency** - Eliminated the need for mocap stages, specialized crews, and large animation teams.
- **Creative agility** - Characters can now respond to cultural moments and trending topics almost as fast as they happen.
- **Audience validation** - The team can build fandom and prove demand before committing to full series production.

As Mark put it:

> Cartwheel makes it possible to create content at the speed of social.

Just as importantly, the model changes how projects grow. Instead of spending as much promoting the show as producing it, the characters themselves become the marketers—showing up regularly, engaging audiences directly, and earning attention through presence rather than spend.

<ArticleVideo cdnKey="dogstar_6.webm" />

We're grateful to Mark Andrews for his vision, collaboration, and willingness to experiment with new production workflows. Special thanks also to Studio Eon for their outstanding character designs that brought the Dogstar universe to life.

Mark Andrews:

- Instagram: [@mandrookz](https://www.instagram.com/mandrookz/)
- LinkedIn: [linkedin.com/in/markandrews4](https://www.linkedin.com/in/markandrews4/)

Studio Eon:

- Website: [studioeon.com](https://www.studioeon.com/)
- LinkedIn: [linkedin.com/company/studio-eon](https://www.linkedin.com/company/studio-eon/)
- Instagram: [@studioeon_01](https://www.instagram.com/studioeon_01/)
- YouTube: [@studioeon9904](https://www.youtube.com/@studioeon9904)
